import { HttpParams } from '@angular/common/http';
import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  CalendarEvent,
  CalendarEventAction,
  CalendarEventTimesChangedEvent,
  CalendarView,
} from 'angular-calendar';
import { EventColor } from 'calendar-utils';
import { isSameDay, isSameMonth } from 'date-fns';
import { Subject } from 'rxjs';

import {
  IBookings,
  IInvitees,
  ITask,
  ITaskForMultipleInvitees,
} from '@shared/interfaces';

import { DIALOG_SIZES } from '../../../../constants';
import { DiaryService } from '../../../../pages/diary/services/diary.service';
import { DateTimeFormatService } from '../../../../services/date-time-format.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../services/snackbar.service';
import { LoggedUserService, UserInfoResponseDTO } from '../../../auth/services';
import { BookingsService } from '../../services/bookings.service';
import { RoomsService } from '../../services/rooms.service';
import { BookingErrorPopupComponent } from '../popups/booking-error-popup/booking-error-popup.component';
import { ReservePopupDialogComponent } from '../popups/reserve-popup/reserve-popup.component';

const colors: Record<string, EventColor> = {
  blue: {
    primary: '#3399ff',
    secondary: '#66b3ff',
  },
  green: {
    primary: '#5dd55d',
    secondary: '#85e085',
  },
};

export enum ACTION {
  DELETE = 'DELETE',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
}

export interface POPUP_DATA {
  date: Date;
  data?: CalendarEvent;
  invitees?: IInvitees[];
  isDisabled: boolean;
  seating_capacity: number;
}

@Component({
  selector: 'app-calendar-booking',
  templateUrl: './calendar-booking.component.html',
  styleUrls: ['./calendar-booking.component.scss'],
})
export class CalendarBookingComponent implements OnInit {
  @Input() roomId: string;
  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any>;

  view: CalendarView = CalendarView.Day;

  CalendarView = CalendarView;

  viewDate: Date = new Date();

  actions: CalendarEventAction[] = [];

  refresh = new Subject<void>();

  events: CalendarEvent[] = [];

  activeDayIsOpen = true;

  isLoading = false;

  invitees: IInvitees[] = [];

  seating_capacity: number;

  today = new Date();

  loggedUserData: UserInfoResponseDTO;

  constructor(
    private dialog: MatDialog,
    private diaryService: DiaryService,
    private dateTimeFormatService: DateTimeFormatService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private bookingsService: BookingsService,
    private roomsService: RoomsService,
    private loggedUserService: LoggedUserService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.getLoggedUser();
    this.getBookings();
    this.getRoomData();
  }

  getLoggedUser() {
    this.loggedUserService.dataStore.subscribe(async (loggedUser) => {
      if (loggedUser) {
        this.loggedUserData = loggedUser;
      }
    });
  }

  getRoomData() {
    this.roomsService.getRoomById(this.route?.snapshot?.params?.id).subscribe({
      next: (res) => {
        if (res.success) {
          this.seating_capacity = res?.data?.seating_capacity;
        }
      },
    });
  }

  getBookings() {
    const httpParams = new HttpParams().set('roomId', this.roomId);
    this.bookingsService.getAllBookings(httpParams).subscribe({
      next: (res) => {
        if (res.count > 0) {
          res.data.results.forEach((booking) => {
            // Push the time range to show on the day time (START)
            this.actions = [];

            if (new Date(booking.date) > this.today) {
              this.actions.push(
                {
                  label: `<span class="reminder-date">${this.dateTimeFormatService.formatDateTime(
                    new Date(booking.from)
                  )} - ${this.dateTimeFormatService.formatDateTime(
                    new Date(booking.to)
                  )}</span>`,
                  a11yLabel: '',
                  onClick: (): void => {
                    // ignore
                  },
                },
                {
                  label:
                    '<span class="material-symbols-outlined">visibility</span>',
                  a11yLabel: 'View',
                  onClick: ({ event }: { event: CalendarEvent }): void => {
                    this.handleEvent(ACTION.VIEW, event);
                  },
                }
              );
              if (this.loggedUserData._id === booking.created_by) {
                this.actions.push(
                  {
                    label:
                      '<span class="material-symbols-outlined">edit</span>',
                    a11yLabel: 'Edit',
                    onClick: ({ event }: { event: CalendarEvent }): void => {
                      this.handleEvent(ACTION.EDIT, event);
                    },
                  },
                  {
                    label:
                      '<span class="material-symbols-outlined">delete</span>',
                    a11yLabel: 'Delete',
                    onClick: ({ event }: { event: CalendarEvent }): void => {
                      this.handleEvent(ACTION.DELETE, event);
                    },
                  }
                );
              }
            } else {
              this.actions.push(
                {
                  label: `<span class="reminder-date">${this.dateTimeFormatService.formatDateTime(
                    new Date(booking.from)
                  )} - ${this.dateTimeFormatService.formatDateTime(
                    new Date(booking.to)
                  )}</span>`,
                  a11yLabel: '',
                  onClick: (): void => {
                    // ignore
                  },
                },
                {
                  label:
                    '<span class="material-symbols-outlined">visibility</span>',
                  a11yLabel: 'View',
                  onClick: ({ event }: { event: CalendarEvent }): void => {
                    this.handleEvent(ACTION.VIEW, event);
                  },
                }
              );
            }

            // Push the time range to show on the day time (END)
            this.events.push({
              id: booking._id.toString(),
              start: new Date(booking.from),
              end: new Date(booking.to),
              title: booking.title,
              color: { ...colors.red },
              actions: this.actions,
              allDay: false,
              resizable: {
                beforeStart: true,
                afterEnd: true,
              },
              draggable: true,
            });

            this.invitees = booking.invitees;

            this.refresh.next();
          });
        }
      },
    });
  }

  clickedSlot(event) {
    if (new Date(event.date) > this.today) {
      const dialogRef = this.dialog.open(ReservePopupDialogComponent, {
        width: DIALOG_SIZES.MEDIUM.maxWidth,
        data: {
          date: event.date,
          isDisabled: false,
          seating_capacity: this.seating_capacity,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result && result.title && result.from && result.to) {
          // Push the time range to show on the day time (START)
          this.actions = [];
          this.actions.push(
            {
              label: `<span class="reminder-date">${this.dateTimeFormatService.formatDateTime(
                new Date(result.from)
              )} - ${this.dateTimeFormatService.formatDateTime(
                new Date(result.to)
              )}</span>`,
              a11yLabel: '',
              onClick: (): void => {
                // ignore
              },
            },
            {
              label:
                '<span class="material-symbols-outlined">visibility</span>',
              a11yLabel: 'View',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.VIEW, event);
              },
            },
            {
              label: '<span class="material-symbols-outlined">edit</span>',
              a11yLabel: 'Edit',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.EDIT, event);
              },
            },
            {
              label: '<span class="material-symbols-outlined">delete</span>',
              a11yLabel: 'Delete',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.DELETE, event);
              },
            }
          );

          // Push the time range to show on the day time (END)
          let isAlreadyBooked = false;
          this.events.forEach((event) => {
            isAlreadyBooked = this.checkForExistingEvents(result, event);
          });

          if (isAlreadyBooked) {
            this.dialog.open(BookingErrorPopupComponent, {
              ...DIALOG_SIZES.MEDIUM,
            });
            return;
          } else {
            const postDate: IBookings = {
              title: result.title,
              date: new Date(result.from),
              from: new Date(result.from),
              to: new Date(result.to),
              invitees: result.invitees,
              roomId: this.roomId,
            };
            this.bookingsService.createBooking(postDate).subscribe({
              next: (res) => {
                if (res.success) {
                  this.events.push({
                    id: res.data._id?.toString(),
                    start: new Date(result.from),
                    end: new Date(result.to),
                    title: result.title,
                    color: { ...colors.red },
                    actions: this.actions,
                    allDay: false,
                    resizable: {
                      beforeStart: true,
                      afterEnd: true,
                    },
                    draggable: true,
                  });

                  this.invitees = result.invitees;

                  this.refresh.next();

                  const users = [];
                  result?.invitees.forEach((invitee) => {
                    users.push(invitee?.id);
                  });
                  const taskData: ITaskForMultipleInvitees = {
                    title: result?.title,
                    start_date: new Date(result.from),
                    end_date: new Date(result.from),
                    start_time: new Date(result?.from),
                    end_time: new Date(result?.to),
                    description: result?.title,
                    all_day: false,
                    relevant_user: users,
                    parent: this.loggedUserData._id,
                  };

                  this.diaryService
                    .addTaskForMultipleInvitees(taskData)
                    .subscribe({
                      next: (resTask) => {
                        const postData: IBookings = {
                          title: result.title,
                          date: new Date(result.from),
                          from: new Date(result.from),
                          to: new Date(result.to),
                          invitees: result.invitees,
                          roomId: this.roomId,
                          taskId: resTask.data[0]._id.toString(),
                        };
                        this.bookingsService
                          .updateBookingsById(postData, res.data._id.toString())
                          .subscribe({
                            next: () => {
                              this.snackbar.success(
                                SUCCESS_TYPES.CREATED,
                                this.translate.instant(
                                  'rooms.popups.reserve.booking'
                                )
                              );
                            },
                          });
                      },
                    });
                }
              },
              error: () => {
                this.dialog.open(BookingErrorPopupComponent, {
                  ...DIALOG_SIZES.MEDIUM,
                });
              },
            });
          }
        }
      });
    }
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    }
  }

  eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
    this.handleEvent('Dropped or resized', event);
  }

  handleEvent(action: string, selectedEvent: CalendarEvent): void {
    if (action === ACTION.VIEW) {
      this.dialog.open(ReservePopupDialogComponent, {
        width: DIALOG_SIZES.MEDIUM.maxWidth,
        data: {
          data: selectedEvent,
          invitees: this.invitees,
          isDisabled: true,
          seating_capacity: this.seating_capacity,
        },
      });
    } else if (action === ACTION.EDIT) {
      const dialogRef = this.dialog.open(ReservePopupDialogComponent, {
        width: DIALOG_SIZES.MEDIUM.maxWidth,
        data: {
          data: selectedEvent,
          invitees: this.invitees,
          isDisabled: false,
          seating_capacity: this.seating_capacity,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const selectedIndex = this.events.indexOf(selectedEvent);

          this.actions = [];
          this.actions.push(
            {
              label: `<span class="reminder-date">${this.dateTimeFormatService.formatDateTime(
                new Date(result.from)
              )} - ${this.dateTimeFormatService.formatDateTime(
                new Date(result.to)
              )}</span>`,
              a11yLabel: '',
              onClick: (): void => {
                // ignore
              },
            },
            {
              label:
                '<span class="material-symbols-outlined">visibility</span>',
              a11yLabel: 'View',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.VIEW, event);
              },
            },
            {
              label: '<span class="material-symbols-outlined">edit</span>',
              a11yLabel: 'Edit',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.EDIT, event);
              },
            },
            {
              label: '<span class="material-symbols-outlined">delete</span>',
              a11yLabel: 'Delete',
              onClick: ({ event }: { event: CalendarEvent }): void => {
                this.handleEvent(ACTION.DELETE, event);
              },
            }
          );

          let isAlreadyBooked = false;
          this.events.forEach((event, index) => {
            if (index === selectedIndex) {
              return;
            }
            isAlreadyBooked = this.checkForExistingEvents(result, event);
          });

          if (isAlreadyBooked) {
            this.dialog.open(BookingErrorPopupComponent, {
              ...DIALOG_SIZES.MEDIUM,
            });
            return;
          } else {
            const updatedDate: IBookings = {
              date: new Date(result.from),
              from: new Date(result.from),
              to: new Date(result.to),
              invitees: result.invitees as IInvitees[],
              title: result.title.toString(),
              roomId: this.roomId,
            };

            this.bookingsService
              .updateBookingsById(updatedDate, result.id)
              .subscribe({
                next: (res) => {
                  this.events[selectedIndex].title = result.title;
                  this.events[selectedIndex].start = new Date(result.from);
                  this.events[selectedIndex].end = new Date(result.to);
                  this.events[selectedIndex].actions = this.actions;
                  this.invitees = result.invitees as IInvitees[];
                  this.refresh.next();

                  const updatedDataTask: ITask = {
                    title: result.title.toString(),
                    start_date: new Date(result.from),
                    end_date: new Date(result.to),
                    start_time: new Date(result?.from),
                    end_time: new Date(result.to),
                    description: result.title.toString(),
                    all_day: false,
                  };

                  this.diaryService
                    .updateTask(updatedDataTask, res.data.taskId.toString())
                    .subscribe({
                      next: () => {
                        this.snackbar.success(
                          SUCCESS_TYPES.UPDATED,
                          this.translate.instant('rooms.popups.reserve.booking')
                        );
                      },
                    });
                },
                error: () => {
                  this.snackbar.error(
                    this.translate.instant('rooms.popups.reserve.update-error')
                  );
                },
              });
          }
        }
      });
    } else if (action === ACTION.DELETE) {
      this.bookingsService
        .deleteBookingsById(selectedEvent.id.toString())
        .subscribe({
          next: (res) => {
            this.events = this.events.filter(
              (event) => event !== selectedEvent
            );
            this.diaryService.deleteTask(res.data.taskId.toString()).subscribe({
              next() {
                this.snackbar.success(
                  SUCCESS_TYPES.DELETED,
                  this.translate.instant('rooms.popups.reserve.booking')
                );
              },
            });
          },
          error: () => {
            this.snackbar.error(
              this.translate.instant('rooms.popups.reserve.delete-error')
            );
          },
        });
    }
  }

  deleteEvent(eventToDelete: CalendarEvent) {
    this.events = this.events.filter((event) => event !== eventToDelete);
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  checkForExistingEvents(result: any, event: CalendarEvent): boolean {
    if (
      new Date(result?.from) < new Date(event?.start) &&
      new Date(result?.to) > new Date(event?.end)
    ) {
      return true;
    } else if (
      new Date(result?.from) <= new Date(event?.start) &&
      new Date(result?.to) < new Date(event?.end) &&
      new Date(result?.to) > new Date(event?.start)
    ) {
      return true;
    } else if (
      new Date(result?.from) > new Date(event?.start) &&
      new Date(result?.from) < new Date(event?.end) &&
      new Date(result?.to) >= new Date(event?.end)
    ) {
      return true;
    } else if (
      new Date(result?.from) >= new Date(event?.start) &&
      new Date(result?.from) < new Date(event?.end) &&
      new Date(result?.to) > new Date(event?.start) &&
      new Date(result?.to) <= new Date(event?.end)
    ) {
      return true;
    } else {
      return false;
    }
  }
}
